import { shapes } from '@joint/plus';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Collapse, FormControlLabel,
    IconButton, Snackbar, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import * as NativeActionService from '../../../../../../../../services/2talk/native-action.service.js';
import { useBaseInspector } from './useBaseInspector';
import NativeActionInputsForm from './NativeActionInputsForm';
import VariableAutocompletePicker from './VariableAutocompletePicker';

/**
 * NativeActionInspector — Item #7 (Fase 1 08/10/2026 → Fase 2 13/10/2026).
 *
 * Caixa SÍNCRONA que executa uma ação nativa do Mexx escolhida de um catálogo seedado via
 * migration (`tb_2talk_chatbot_native_action_catalog`).
 *
 * UX (Fase 2 — padrão Zapier/Make/n8n):
 *   1. Dropdown de slug (categorizado por module via `groupBy`)
 *   2. Após escolher: sincroniza portas no canvas via `addPort`/`removePorts` conforme
 *      `exit_ports_json` do slug — padrão idêntico ao ConditionInspector. IDs do JOINT
 *      são gerados por porta e persistidos em `attrs/portMap/text` ({"success":"...","error":"..."})
 *      pro backend mapear ida.
 *   3. Form DINÂMICO renderizado a partir de `input_schema_json` (via NativeActionInputsForm) —
 *      admin NÃO vê JSON cru; vê 1 campo por property do schema, cada um com botão {{}} pra
 *      inserir variável. Fallback "Modo avançado" volta pra textarea JSON cru (escape hatch).
 *   4. Toggle "Ver contrato (JSON Schema)" expõe o schema cru colapsável pra admin avançado.
 *   5. "Exemplo preenchido" sugere payload com {{variable.<nome>}} em cada property pra copiar.
 *
 * Persistência: `attrs/slug/text`, `attrs/inputs/text`, `attrs/portMap/text`.
 * Backend: ChatbotFlowController case FlowTypeNativeAction → INativeActionBoxService.
 */

interface Props {
    cell: shapes.app.Base;
    chatbotFlowId: string;
}

type CatalogEntry = {
    id: number;
    slug: string;
    module: string;
    display_name: string;
    description?: string;
    input_schema_json?: string;
    output_schema_json?: string;
    exit_ports_json?: string;
};

type ExitPort = { id: string; label: string };

const cellProps = {
    label: ['attrs', 'label', 'text'],
    slug: ['attrs', 'slug', 'text'],
    inputs: ['attrs', 'inputs', 'text'],
    portMap: ['attrs', 'portMap', 'text'],
    resultVariable: ['attrs', 'resultVariable', 'text'],
};

// Rótulos amigáveis dos modules — usados pelo `groupBy` do Autocomplete.
const MODULE_LABELS: Record<string, string> = {
    called: '🎫 Chamados',
    chat: '💬 Chat',
    user: '👤 Usuário',
    attachment: '📎 Anexos',
};

const genPortId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const NativeActionInspector = (props: Props): ReactElement => {
    const { cell } = props;
    const intl = useIntl();

    const [label, setLabel] = useState<string>('');
    const [slug, setSlug] = useState<string>('');
    const [inputsJson, setInputsJson] = useState<string>('{}');
    const [resultVariable, setResultVariable] = useState<string>('');
    const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // Feedback "Copiado!" ao clicar no botão de copy.
    const [copied, setCopied] = useState<boolean>(false);

    // Fase 2 (13/10/2026) — Toggles:
    //   - advancedMode: volta pra textarea JSON cru (escape hatch pro form dinâmico).
    //   - contractOpen: mostra o schema cru em colapsável pra admin avançado.
    const [advancedMode, setAdvancedMode] = useState<boolean>(false);
    const [contractOpen, setContractOpen] = useState<boolean>(false);

    const isSyncingRef = useRef(false);

    const loadCatalog = useCallback(async () => {
        setLoading(true);
        try {
            const resp: any = await NativeActionService.getAll();
            const raw: any[] = Array.isArray(resp?.data) ? resp.data : [];
            const list: CatalogEntry[] = raw.map((c) => ({
                id: Number(c.id ?? c.Id ?? 0),
                slug: String(c.slug ?? c.Slug ?? ''),
                module: String(c.module ?? c.Module ?? ''),
                display_name: String(c.display_name ?? c.displayName ?? c.DisplayName ?? ''),
                description: c.description ?? c.Description,
                input_schema_json: c.input_schema_json ?? c.inputSchemaJson ?? c.InputSchemaJson,
                output_schema_json: c.output_schema_json ?? c.outputSchemaJson ?? c.OutputSchemaJson,
                exit_ports_json: c.exit_ports_json ?? c.exitPortsJson ?? c.ExitPortsJson,
            })).filter((c) => c.id > 0 && c.slug);
            setCatalog(list);
        } catch {
            setCatalog([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadCatalog(); }, [loadCatalog]);

    const assignFormFields = useCallback((): void => {
        if (isSyncingRef.current) return;
        setLabel(cell.prop(cellProps.label) ?? '');
        setSlug(cell.prop(cellProps.slug) ?? '');
        setInputsJson(cell.prop(cellProps.inputs) ?? '{}');
        setResultVariable(cell.prop(cellProps.resultVariable) ?? '');
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    const persist = (path: string[], value: string) => {
        isSyncingRef.current = true;
        try { changeCellProp(path, value); }
        finally { queueMicrotask(() => { isSyncingRef.current = false; }); }
    };

    /**
     * Reconstrói as portas OUT do cell conforme o exit_ports_json do slug escolhido.
     * Padrão idêntico ao ConditionInspector.syncPortsAndPersist:
     *   a) Remove órfãs (portas antigas que não estão mais no slug novo)
     *   b) Adiciona ausentes (com label do catálogo)
     *   c) Atualiza labels das existentes
     *   d) Grava portMap {catalog_port_id → joint_port_id} em attrs
     */
    const syncPortsForSlug = (entry: CatalogEntry | null) => {
        isSyncingRef.current = true;
        try {
            let exitPorts: ExitPort[] = [];
            if (entry?.exit_ports_json) {
                try {
                    const parsed = JSON.parse(entry.exit_ports_json);
                    if (Array.isArray(parsed)) {
                        exitPorts = parsed
                            .filter((p) => p?.id)
                            .map((p) => ({ id: String(p.id), label: String(p.label ?? p.id) }));
                    }
                } catch { /* schema corrompido no seed — ignora */ }
            }

            const currentOut: any[] = (cell as any).getGroupPorts('out') ?? [];
            // Reusa portMap atual pra PRESERVAR joint_port_id das portas que persistem (evita
            // perder links já conectados no canvas).
            let currentMap: Record<string, string> = {};
            try {
                const raw = cell.prop(cellProps.portMap);
                if (raw) currentMap = JSON.parse(raw);
            } catch { /* vazio */ }

            const desiredCatalogIds = new Set(exitPorts.map((p) => p.id));

            // a) Remove órfãs
            const toRemove = currentOut.filter((p) => {
                const catalogId = Object.entries(currentMap).find(([_, jointId]) => jointId === p.id)?.[0];
                return !catalogId || !desiredCatalogIds.has(catalogId);
            });
            if (toRemove.length > 0) (cell as any).removePorts(toRemove);

            // b/c) Adiciona ausentes e atualiza labels + remonta map
            const newMap: Record<string, string> = {};
            for (const ep of exitPorts) {
                const existingJointId = currentMap[ep.id];
                const stillThere = existingJointId
                    ? ((cell as any).getGroupPorts('out') ?? []).some((p: any) => p.id === existingJointId)
                    : false;

                if (stillThere && existingJointId) {
                    newMap[ep.id] = existingJointId;
                    (cell as any).portProp(existingJointId, ['attrs', 'portLabel', 'text'], ep.label);
                } else {
                    const newJointId = genPortId(ep.id);
                    (cell as any).addPort({
                        id: newJointId,
                        group: 'out',
                        attrs: { portLabel: { text: ep.label } },
                    });
                    newMap[ep.id] = newJointId;
                }
            }

            // d) Persiste portMap
            changeCellProp(cellProps.portMap, JSON.stringify(newMap));
        } finally {
            queueMicrotask(() => { isSyncingRef.current = false; });
        }
    };

    const handleSlugChange = (next: CatalogEntry | null) => {
        const newSlug = next?.slug ?? '';
        setSlug(newSlug);
        persist(cellProps.slug, newSlug);
        syncPortsForSlug(next);
        // Reset inputs ao trocar de slug — schemas diferem entre slugs.
        setInputsJson('{}');
        persist(cellProps.inputs, '{}');
    };

    const selectedEntry = catalog.find((c) => c.slug === slug) ?? null;
    const isMissing = !loading && !!slug && !selectedEntry;

    // Validação simples do JSON de inputs (usada APENAS no modo avançado — form dinâmico
    // garante JSON válido por construção).
    let inputsJsonError: string | null = null;
    try {
        if (inputsJson.trim()) JSON.parse(inputsJson);
    } catch (e: any) {
        inputsJsonError = e?.message ?? 'JSON inválido';
    }

    // Fase 2 — parsing do schema da ação selecionada + inputs atuais em objetos JS.
    // Se qualquer parse falha, o form dinâmico fica indisponível e o modo avançado é forçado.
    const parsedSchema = useMemo<any>(() => {
        if (!selectedEntry?.input_schema_json) return null;
        try { return JSON.parse(selectedEntry.input_schema_json); }
        catch { return null; }
    }, [selectedEntry?.input_schema_json]);

    const parsedInputs = useMemo<Record<string, unknown>>(() => {
        try {
            const o = JSON.parse(inputsJson || '{}');
            return (o && typeof o === 'object' && !Array.isArray(o)) ? o : {};
        } catch { return {}; }
    }, [inputsJson]);

    // Form dinâmico só pode entrar se temos schema válido com `properties`, inputs parseiam
    // como objeto, e admin não forçou modo avançado.
    const hasValidSchema = !!(parsedSchema && typeof parsedSchema === 'object' && parsedSchema.properties);
    const canUseForm = hasValidSchema && !inputsJsonError && !advancedMode;

    // Gera payload-exemplo DILUÍDO com {{variable.<key>}} em cada property do schema. Serve
    // de "copia-cola" didático pro admin — substitui o schema cru visível.
    const exampleFilled = useMemo<string>(() => {
        if (!hasValidSchema) return '';
        const buildExample = (sch: any, pathHint = ''): any => {
            if (!sch || typeof sch !== 'object') return '';
            if (Array.isArray(sch.enum) && sch.enum.length > 0) return sch.enum[0];
            if (sch.type === 'boolean') return `{{variable.${pathHint || 'flag'}}}`;
            if (sch.type === 'integer' || sch.type === 'number') return `{{variable.${pathHint || 'numero'}}}`;
            if (sch.type === 'array') return [`{{variable.${pathHint || 'item1'}}}`];
            if (sch.type === 'object' && sch.properties) {
                const nested: Record<string, any> = {};
                for (const k of Object.keys(sch.properties)) {
                    nested[k] = buildExample(sch.properties[k], k);
                }
                return nested;
            }
            return `{{variable.${pathHint || 'valor'}}}`;
        };
        const out: Record<string, any> = {};
        for (const k of Object.keys(parsedSchema.properties)) {
            out[k] = buildExample(parsedSchema.properties[k], k);
        }
        return JSON.stringify(out, null, 2);
    }, [parsedSchema, hasValidSchema]);

    // Schema cru (bonito) — usado no toggle "Ver contrato (JSON Schema)".
    const rawSchemaPretty = useMemo<string>(() => {
        if (!selectedEntry?.input_schema_json) return '';
        try { return JSON.stringify(JSON.parse(selectedEntry.input_schema_json), null, 2); }
        catch { return selectedEntry.input_schema_json; }
    }, [selectedEntry?.input_schema_json]);

    // 13/10/2026 — Output schema visível pra admin entender o SHAPE do que a caixa GRAVA
    // na variável de resultado. Crítico pra caixas que retornam objeto — admin precisa
    // saber que `{{variable.X.id}}`, `{{variable.X.customer.email}}` são as formas de
    // acessar campos aninhados em caixas downstream.
    const outputSchemaPretty = useMemo<string>(() => {
        if (!selectedEntry?.output_schema_json) return '';
        try { return JSON.stringify(JSON.parse(selectedEntry.output_schema_json), null, 2); }
        catch { return selectedEntry.output_schema_json; }
    }, [selectedEntry?.output_schema_json]);

    // Dica dinâmica de dot-notation construída do output schema. Mostra 1 exemplo
    // concreto baseado nas properties do root — "{{variable.<resultVariable>.<primeira_prop>}}".
    const outputDotNotationHint = useMemo<string>(() => {
        if (!selectedEntry?.output_schema_json) return '';
        try {
            const parsed = JSON.parse(selectedEntry.output_schema_json);
            const props = parsed?.properties;
            if (!props || typeof props !== 'object') return '';
            const firstKey = Object.keys(props)[0];
            if (!firstKey) return '';
            const varName = resultVariable || '<variável>';
            return `{{variable.${varName}.${firstKey}}}`;
        } catch { return ''; }
    }, [selectedEntry?.output_schema_json, resultVariable]);

    const requiredKeys = useMemo<string[]>(() => {
        if (!parsedSchema) return [];
        return Array.isArray(parsedSchema.required) ? parsedSchema.required : [];
    }, [parsedSchema]);

    const copyExample = () => {
        navigator.clipboard?.writeText(exampleFilled).then(() => setCopied(true)).catch(() => {});
    };
    const copySchema = () => {
        navigator.clipboard?.writeText(rawSchemaPretty).then(() => setCopied(true)).catch(() => {});
    };

    // Form → shape: recebe values object, serializa pra JSON string, persiste.
    const onFormChange = (next: Record<string, unknown>) => {
        const serialized = JSON.stringify(next);
        setInputsJson(serialized);
        persist(cellProps.inputs, serialized);
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
                        {intl.formatMessage({ id: 'na.title', defaultMessage: 'Ação Nativa' })}
                    </Typography>
                    <Tooltip title={intl.formatMessage({
                        id: 'na.title.hint',
                        defaultMessage: 'Executa uma ação nativa do Mexx (listar chamados, criar acompanhamento, transferir equipe, etc.) do catálogo oficial. Portas de saída variam por ação.',
                    })}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                </Stack>

                <TextField
                    label={intl.formatMessage({ id: 'na.label', defaultMessage: 'Rótulo (opcional)' })}
                    size="small"
                    value={label}
                    onChange={(e) => {
                        setLabel(e.target.value);
                        changeCellProp(cellProps.label, e.target.value);
                    }}
                    placeholder="Ação Nativa"
                />

                <Autocomplete<CatalogEntry, false, false, false>
                    size="small"
                    options={catalog}
                    value={selectedEntry}
                    loading={loading}
                    loadingText={intl.formatMessage({ id: 'na.loading', defaultMessage: 'Carregando catálogo...' })}
                    noOptionsText={intl.formatMessage({ id: 'na.empty', defaultMessage: 'Catálogo vazio.' })}
                    groupBy={(opt) => MODULE_LABELS[opt.module] ?? opt.module}
                    getOptionLabel={(o) => o?.display_name ?? ''}
                    isOptionEqualToValue={(a, b) => a?.slug === b?.slug}
                    disabled={loading}
                    onChange={(_, next) => handleSlugChange(next)}
                    renderOption={(propsOpt, option) => (
                        <li {...propsOpt} key={option.id}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.display_name}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                                    {option.slug}
                                </Typography>
                                {option.description && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                        {option.description}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={intl.formatMessage({ id: 'na.action', defaultMessage: 'Ação' })}
                            placeholder={loading
                                ? intl.formatMessage({ id: 'na.loading', defaultMessage: 'Carregando catálogo...' })
                                : intl.formatMessage({ id: 'na.action.placeholder', defaultMessage: 'Escolha uma ação do catálogo' })}
                            error={isMissing}
                            helperText={isMissing
                                ? intl.formatMessage({
                                    id: 'na.action.missing',
                                    defaultMessage: 'Slug "{slug}" não existe mais no catálogo. Escolha outro.',
                                }, { slug })
                                : undefined}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />

                {selectedEntry && (
                    <Alert severity="info" sx={{ fontSize: 12, py: 0.5 }}>
                        <strong>Portas de saída:</strong>{' '}
                        {(() => {
                            try {
                                const ports: ExitPort[] = JSON.parse(selectedEntry.exit_ports_json ?? '[]');
                                return (
                                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                                        {ports.map((p) => (
                                            <Chip key={p.id} label={p.label} size="small" variant="outlined" />
                                        ))}
                                    </Stack>
                                );
                            } catch {
                                return <em>schema inválido</em>;
                            }
                        })()}
                    </Alert>
                )}

                {/* ========= Inputs — form dinâmico (padrão Zapier) ou textarea avançado ========= */}
                {selectedEntry && (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                                {intl.formatMessage({ id: 'na.inputs.title', defaultMessage: 'Preenchimento' })}
                            </Typography>
                            {hasValidSchema && (
                                <FormControlLabel
                                    control={<Switch
                                        size="small"
                                        checked={advancedMode}
                                        onChange={(_, next) => setAdvancedMode(next)}
                                    />}
                                    label={<Typography variant="caption">{intl.formatMessage({
                                        id: 'na.advanced.toggle',
                                        defaultMessage: 'Modo avançado (JSON)',
                                    })}</Typography>}
                                    sx={{ m: 0 }}
                                />
                            )}
                        </Stack>

                        {requiredKeys.length > 0 && !advancedMode && (
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary' }}>
                                {intl.formatMessage({ id: 'na.schema.required', defaultMessage: 'Obrigatórios:' })}{' '}
                                {requiredKeys.map((k) => (
                                    <Chip key={k} label={k} size="small" sx={{ mr: 0.5, height: 18, fontSize: 10 }} />
                                ))}
                            </Typography>
                        )}

                        {canUseForm ? (
                            <NativeActionInputsForm
                                schema={parsedSchema}
                                values={parsedInputs}
                                onChange={onFormChange}
                                cell={cell}
                            />
                        ) : (
                            <>
                                {!hasValidSchema && (
                                    <Alert severity="warning" sx={{ fontSize: 12, py: 0.5, mb: 0.5 }}>
                                        {intl.formatMessage({
                                            id: 'na.form.noSchema',
                                            defaultMessage: 'Esta ação não tem schema de inputs no catálogo — usando modo avançado (JSON cru).',
                                        })}
                                    </Alert>
                                )}
                                <TextField
                                    size="small"
                                    multiline
                                    fullWidth
                                    minRows={4}
                                    maxRows={10}
                                    value={inputsJson}
                                    onChange={(e) => {
                                        setInputsJson(e.target.value);
                                        persist(cellProps.inputs, e.target.value);
                                    }}
                                    error={!!inputsJsonError}
                                    helperText={inputsJsonError
                                        ?? intl.formatMessage({
                                            id: 'na.inputs.help',
                                            defaultMessage: 'JSON com os parâmetros da ação. Suporta placeholders em strings (ex.: {"called_id":"{{variable.ticket_id}}"}).',
                                        })}
                                    placeholder='{"status":"Em andamento"}'
                                    sx={{ '& textarea': { fontFamily: '"Fira Code", "Consolas", monospace', fontSize: 12 } }}
                                />
                            </>
                        )}
                    </Box>
                )}

                {/* ========= Exemplo preenchido (payload diluído com placeholders) ========= */}
                {hasValidSchema && exampleFilled && (
                    <Box>
                        <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ fontSize: 12, py: 0.5, mb: 0.5 }}>
                            {intl.formatMessage({
                                id: 'na.example.help',
                                defaultMessage: 'Modelo didático com placeholders — copie e cole se preferir editar o JSON diretamente no Modo avançado.',
                            })}
                        </Alert>
                        <Box sx={{ background: '#1e1e2e', color: '#e0e0e0', fontFamily: 'monospace', fontSize: 11, p: 1, borderRadius: 1, position: 'relative' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: '#e0e0e0', fontWeight: 500 }}>
                                    {intl.formatMessage({ id: 'na.example.label', defaultMessage: 'Exemplo preenchido:' })}
                                </Typography>
                                <Tooltip title={intl.formatMessage({ id: 'na.example.copy', defaultMessage: 'Copiar exemplo' })}>
                                    <IconButton size="small" onClick={copyExample} sx={{ color: '#e0e0e0', p: 0.25 }}>
                                        <ContentCopyIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            <pre
                                style={{
                                    margin: 0,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    userSelect: 'text',
                                    WebkitUserSelect: 'text',
                                    cursor: 'text',
                                }}
                            >
                                {exampleFilled}
                            </pre>
                        </Box>
                    </Box>
                )}

                {/* ========= Toggle "Ver contrato (JSON Schema)" ========= */}
                {hasValidSchema && (
                    <Box>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={contractOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            endIcon={<CodeIcon fontSize="small" />}
                            onClick={() => setContractOpen((v) => !v)}
                            sx={{ textTransform: 'none', color: 'text.secondary' }}
                        >
                            {contractOpen
                                ? intl.formatMessage({ id: 'na.contract.hide', defaultMessage: 'Esconder contrato (JSON Schema)' })
                                : intl.formatMessage({ id: 'na.contract.show', defaultMessage: 'Ver contrato (JSON Schema)' })}
                        </Button>
                        <Collapse in={contractOpen}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                {intl.formatMessage({
                                    id: 'na.contract.help',
                                    defaultMessage: 'Schema técnico do backend: define types/required/enum de cada input. Usado pra validação server-side. Admin padrão não precisa olhar isso — está aqui pra integradores/debug.',
                                })}
                            </Typography>
                            <Box sx={{ background: '#1e1e2e', color: '#e0e0e0', fontFamily: 'monospace', fontSize: 11, p: 1, borderRadius: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: '#e0e0e0', fontWeight: 500 }}>
                                        {intl.formatMessage({ id: 'na.schema.input.label', defaultMessage: 'Contrato de INPUT (o que a caixa espera):' })}
                                    </Typography>
                                    <Tooltip title={intl.formatMessage({ id: 'na.schema.copy', defaultMessage: 'Copiar contrato' })}>
                                        <IconButton size="small" onClick={copySchema} sx={{ color: '#e0e0e0', p: 0.25 }}>
                                            <ContentCopyIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <pre
                                    style={{
                                        margin: 0,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        userSelect: 'text',
                                        WebkitUserSelect: 'text',
                                        cursor: 'text',
                                    }}
                                >
                                    {rawSchemaPretty}
                                </pre>
                            </Box>

                            {outputSchemaPretty && (
                                <Box sx={{ background: '#1e1e2e', color: '#e0e0e0', fontFamily: 'monospace', fontSize: 11, p: 1, borderRadius: 1, mt: 1 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: '#e0e0e0', fontWeight: 500 }}>
                                            {intl.formatMessage({
                                                id: 'na.schema.output.label',
                                                defaultMessage: 'Contrato de OUTPUT (o que a caixa GRAVA na variável de resultado):',
                                            })}
                                        </Typography>
                                    </Stack>
                                    <pre
                                        style={{
                                            margin: 0,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            userSelect: 'text',
                                            WebkitUserSelect: 'text',
                                            cursor: 'text',
                                        }}
                                    >
                                        {outputSchemaPretty}
                                    </pre>
                                </Box>
                            )}
                        </Collapse>
                    </Box>
                )}

                {/* ========= Dica de dot-notation (só quando há output schema + resultVariable) ========= */}
                {outputDotNotationHint && resultVariable && (
                    <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ fontSize: 12, py: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            {intl.formatMessage({
                                id: 'na.output.dotNotationTitle',
                                defaultMessage: 'Como ler o resultado em caixas seguintes:',
                            })}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.25 }}>
                            {intl.formatMessage({
                                id: 'na.output.dotNotationBody',
                                defaultMessage: 'Esta caixa grava um OBJETO em "{var}". Pra acessar um campo específico depois (ex.: numa condição, numa próxima ação, numa mensagem), use dot-notation:',
                            }, { var: resultVariable })}
                        </Typography>
                        <Box sx={{ background: '#1e1e2e', color: '#e0e0e0', fontFamily: 'monospace', fontSize: 11, px: 1, py: 0.5, borderRadius: 1, mt: 0.5, display: 'inline-block' }}>
                            {outputDotNotationHint}
                        </Box>
                    </Alert>
                )}

                <VariableAutocompletePicker
                    value={resultVariable}
                    onChange={(name) => {
                        setResultVariable(name);
                        persist(cellProps.resultVariable, name);
                    }}
                    label={intl.formatMessage({ id: 'na.resultVariable', defaultMessage: 'Salvar resultado em variável (opcional)' })}
                    placeholder={intl.formatMessage({ id: 'na.resultVariable.placeholder', defaultMessage: 'Selecione uma variável (ou deixe vazio)' })}
                    helperText={intl.formatMessage({
                        id: 'na.resultVariable.help',
                        defaultMessage: 'Nome de uma variável cadastrada. Se preenchido, o payload JSON retornado pelo handler é gravado nessa variável — próximas caixas podem usar via @nome@ ou {{variable.nome}}.',
                    })}
                />
            </Box>

            <Snackbar
                open={copied}
                autoHideDuration={1800}
                onClose={() => setCopied(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                message={intl.formatMessage({ id: 'na.copied', defaultMessage: 'Copiado!' })}
            />
        </>
    );
};

export default NativeActionInspector;
